import gql from 'graphql-tag';
import { VideoFields, LinkFields } from '../common-fields.queries';

const FacetItemFields = gql`
	fragment FacetItemFields on FacetItem {
		id
		value
		groupName
		range {
			min
			max
		}
	}
`;

const ConstructImageFields = gql`
	fragment ConstructImageFields on Image {
		id
		imageType
		description
		lowQualityPlaceholder
	}
`;

const AuthorProfileFields = gql`
	${ConstructImageFields}

	fragment AuthorProfileFields on AuthorProfile {
		id
		name
		avatar {
			...ConstructImageFields
		}
	}
`;

const BrandFields = gql`
	${ConstructImageFields}
	fragment BrandFields on Brand {
		logo {
			...ConstructImageFields
		}
		name
	}
`;

const ContentProductCardDetailFields = gql`
	${ConstructImageFields}

	fragment ContentProductCardDetailFields on ContentProductCardDetail {
		finishId
		manufacturer
		description
		model
		finishes {
			id
			title
		}
		startingPrice
		averageRating
		reviewCount
	}
`;

// Create a fragment with the fields needed for the CardContent type.
const ContentCardCommonFields = gql`
	${AuthorProfileFields}
	${FacetItemFields}
	${ConstructImageFields}
	${VideoFields}
	${LinkFields}
	${BrandFields}
	${ContentProductCardDetailFields}

	fragment ContentCardCommonFields on ContentCardValue {
		cardType
		image {
			...ConstructImageFields
		}
		title
		description
		url
		trackingCode
		detail {
			... on ContentArticleCardDetail {
				video {
					...VideoFields
				}
			}
			... on ContentCategoryCardDetail {
				facets {
					...FacetItemFields
				}
			}
			... on ContentFavoriteCardDetail {
				author {
					...AuthorProfileFields
				}
				totalProducts
				productImages {
					...ConstructImageFields
				}
			}
			... on ContentProductCardDetail {
				...ContentProductCardDetailFields
			}
			... on ContentProfileCardDetail {
				profile {
					...AuthorProfileFields
				}
			}
			... on ContentVideoCardDetail {
				video {
					...VideoFields
				}
			}
			... on ContentBrandCardDetail {
				brand {
					...BrandFields
				}
				link {
					...LinkFields
				}
			}
		}
	}
`;

const ContentCardValueFields = gql`
	${ContentCardCommonFields}

	fragment ContentCardValueFields on ContentCardValue {
		...ContentCardCommonFields
		fieldIndices
	}
`;

const ContentImageValueFields = gql`
	${ConstructImageFields}

	fragment ContentImageValueFields on ContentImageValue {
		image {
			...ConstructImageFields
		}
	}
`;

const ContentLinkValueFields = gql`
	fragment ContentLinkValueFields on ContentLinkValue {
		linkUrl
		trackingCode
	}
`;

const ContentListValueFields = gql`
	fragment ContentListValueFields on ContentListValue {
		itemIndices
	}
`;

const ContentObjectValueFields = gql`
	fragment ContentObjectValueFields on ContentObjectValue {
		fieldIndices
	}
`;

const ContentTextValueFields = gql`
	fragment ContentTextValueFields on ContentTextValue {
		text
	}
`;

const ContentRichTextValueFields = gql`
	fragment ContentRichTextValueFields on ContentRichTextValue {
		richText
	}
`;

// This is broken into fragments to make it easier to make client-friendly content structure types.
// See src/types/construct.types.ts.
export const ContentDocumentFields = gql`
	${ContentCardValueFields}
	${ContentImageValueFields}
	${ContentListValueFields}
	${ContentLinkValueFields}
	${ContentObjectValueFields}
	${ContentRichTextValueFields}
	${ContentTextValueFields}

	fragment ContentDocumentFields on ContentDocument {
		id
		rootIndex
		kinds
		fieldNames
		values {
			... on ContentCardValue {
				...ContentCardValueFields
			}
			... on ContentImageValue {
				...ContentImageValueFields
			}
			... on ContentLinkValue {
				...ContentLinkValueFields
			}
			... on ContentListValue {
				...ContentListValueFields
			}
			... on ContentObjectValue {
				...ContentObjectValueFields
			}
			... on ContentRichTextValue {
				...ContentRichTextValueFields
			}
			... on ContentTextValue {
				...ContentTextValueFields
			}
		}
	}
`;

export const SUPPORT_PAGE_DOCUMENTS = gql`
	${ContentDocumentFields}
	query SupportPageDocuments($input: PageDocumentCriteria!) {
		supportMenuDocument {
			...ContentDocumentFields
		}
		pageDocument(criteria: $input) {
			...ContentDocumentFields
		}
	}
`;

export const LANDING_PAGE = gql`
	${ContentDocumentFields}
	query LandingPage($input: PageDocumentCriteria!) {
		pageDocument(criteria: $input) {
			...ContentDocumentFields
		}
	}
`;
